import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarCheck,
  UserPlus,
  MessageSquare,
  Users,
  Sparkles,
  ArrowLeft,
  Settings as SettingsIcon,
  PhoneCall,
  LogOut,
  Play,
  Loader2,
  Mail,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { API } from "../lib/api";
import { authHeaders, useAuth } from "../context/AuthContext";

const CHART_TOOLTIP_STYLE = { background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 6, color: "#F5F5F5" };
const CHART_CURSOR = { fill: "rgba(212,175,55,0.08)" };
const CHART_BAR_RADIUS = [4, 4, 0, 0];

const navItems = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "appointments", label: "Appointments", icon: CalendarCheck },
  { key: "applicants", label: "Applicants", icon: UserPlus },
  { key: "leads", label: "Leads", icon: Users },
  { key: "inbox", label: "Inbox", icon: Mail },
  { key: "calls", label: "Phone Calls", icon: PhoneCall },
  { key: "transcripts", label: "Transcripts", icon: MessageSquare },
  { key: "settings", label: "Voice Settings", icon: SettingsIcon },
];

const StatCard = ({ label, value, testid }) => (
  <div className="bg-[#141414] border border-[#27272A] rounded-lg p-6" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.18em] text-[#A3A3A3] mb-2">{label}</p>
    <p className="font-display text-4xl text-[#D4AF37]">{value ?? 0}</p>
  </div>
);

const Th = ({ children }) => (
  <th className="text-left text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-semibold px-4 py-3 border-b border-[#27272A]">
    {children}
  </th>
);
const Td = ({ children }) => (
  <td className="px-4 py-3 text-sm text-[#F5F5F5] border-b border-[#1c1c1c]">{children}</td>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [leads, setLeads] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [calls, setCalls] = useState([]);
  const [emails, setEmails] = useState([]);
  const [emailDrafts, setEmailDrafts] = useState({});
  const [sendingEmail, setSendingEmail] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState("shimmer");
  const [speed, setSpeed] = useState(0.95);
  const [savingVoice, setSavingVoice] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    const h = { headers: authHeaders(), credentials: "include" };
    const load = async () => {
      try {
        const [s, a, ap, l, t, c, v, st, em] = await Promise.all([
          fetch(`${API}/dashboard/stats`, h).then((r) => r.json()),
          fetch(`${API}/appointments`, h).then((r) => r.json()),
          fetch(`${API}/applicants`, h).then((r) => r.json()),
          fetch(`${API}/leads`, h).then((r) => r.json()),
          fetch(`${API}/dashboard/transcripts`, h).then((r) => r.json()),
          fetch(`${API}/calls`, h).then((r) => r.json()),
          fetch(`${API}/voices`).then((r) => r.json()),
          fetch(`${API}/settings`).then((r) => r.json()),
          fetch(`${API}/emails`, h).then((r) => r.json()),
        ]);
        setStats(s);
        setAppointments(Array.isArray(a) ? a : []);
        setApplicants(Array.isArray(ap) ? ap : []);
        setLeads(Array.isArray(l) ? l : []);
        setTranscripts(Array.isArray(t) ? t : []);
        setCalls(Array.isArray(c) ? c : []);
        setVoices(v.voices || []);
        setVoice(st.voice || "shimmer");
        setSpeed(st.speed || 0.95);
        const emailList = Array.isArray(em) ? em : [];
        setEmails(emailList);
        setEmailDrafts(Object.fromEntries(emailList.map((e) => [e.id, e.draft_reply || ""])));
      } catch {
        toast.error("Could not load dashboard data.");
      }
    };
    load();
  }, []);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const saveVoice = async () => {
    setSavingVoice(true);
    try {
      const res = await fetch(`${API}/settings`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ voice, speed }),
      });
      if (!res.ok) throw new Error();
      toast.success("Seraphina's voice updated.");
    } catch {
      toast.error("Could not save voice settings.");
    } finally {
      setSavingVoice(false);
    }
  };

  const previewVoice = async () => {
    setPreviewing(true);
    try {
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: "Hello darling, I'm Seraphina, your Guardian Angel. It's a pleasure to hear from you.", voice }),
      });
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audio.playbackRate = 1;
      audio.onended = () => setPreviewing(false);
      await audio.play();
    } catch {
      toast.error("Preview failed.");
      setPreviewing(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/appointments/${id}/status`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Marked ${status}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const [sendingSummary, setSendingSummary] = useState(false);
  const sendSummary = async () => {
    setSendingSummary(true);
    try {
      const res = await fetch(`${API}/dashboard/send-summary`, { method: "POST", credentials: "include", headers: authHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const c = data.counts || {};
      toast.success(`Recap emailed — ${(c.heaven_on_earth||0)+(c.special_events||0)} bookings, ${c.applicants||0} applicants, ${c.calls||0} calls`);
    } catch {
      toast.error("Could not send recap.");
    } finally {
      setSendingSummary(false);
    }
  };

  const sendReply = async (id) => {
    setSendingEmail(id);
    try {
      const res = await fetch(`${API}/emails/${id}/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ reply: emailDrafts[id] || "" }),
      });
      if (!res.ok) throw new Error();
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, status: "sent" } : e)));
      toast.success("Reply sent to guest.");
    } catch {
      toast.error("Could not send reply.");
    } finally {
      setSendingEmail(null);
    }
  };

  const ignoreEmail = async (id) => {
    try {
      const res = await fetch(`${API}/emails/${id}/ignore`, { method: "POST", credentials: "include", headers: authHeaders() });
      if (!res.ok) throw new Error();
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, status: "ignored" } : e)));
      toast("Email ignored.");
    } catch {
      toast.error("Could not update.");
    }
  };

  const chartData = [
    { name: "Special", value: stats.special_events || 0 },
    { name: "Heaven", value: stats.heaven_on_earth || 0 },
    { name: "Applicants", value: stats.applicants || 0 },
    { name: "Leads", value: stats.leads || 0 },
    { name: "Chats", value: stats.chat_sessions || 0 },
  ];

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-[#27272A] bg-[#0A0A0A] flex flex-col p-5 fixed h-screen">
        <div className="flex items-center gap-2 mb-10">
          <Sparkles className="text-[#D4AF37]" size={20} />
          <span className="font-display text-lg">Angel Desk</span>
        </div>
        <nav className="space-y-1 flex-1">
          {navItems.map((n) => (
            <button
              key={n.key}
              onClick={() => setTab(n.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                tab === n.key
                  ? "bg-[#141414] text-[#D4AF37] border border-[#27272A]"
                  : "text-[#A3A3A3] hover:text-[#F5F5F5]"
              }`}
              data-testid={`nav-${n.key}`}
            >
              <n.icon size={17} />
              {n.label}
            </button>
          ))}
        </nav>
        <div className="mt-4 space-y-2">
          <p className="text-xs text-[#6b6b6b] px-1 truncate" data-testid="admin-user-email">{user?.email}</p>
          <button onClick={doLogout} className="w-full flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#D4AF37] transition-colors" data-testid="logout-button">
            <LogOut size={16} /> Sign out
          </button>
          <Link to="/" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#D4AF37] transition-colors" data-testid="back-to-site">
            <ArrowLeft size={16} /> Back to site
          </Link>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 ml-60 p-10">
        <h1 className="font-display text-4xl font-light mb-8 capitalize" data-testid="admin-title">
          {tab}
        </h1>

        {tab === "overview" && (
          <div className="space-y-8" data-testid="overview-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
              <StatCard label="Appointments" value={stats.appointments} testid="stat-appointments" />
              <StatCard label="Special Events" value={stats.special_events} testid="stat-special" />
              <StatCard label="Heaven on Earth" value={stats.heaven_on_earth} testid="stat-heaven" />
              <StatCard label="Applicants" value={stats.applicants} testid="stat-applicants" />
              <StatCard label="Leads" value={stats.leads} testid="stat-leads" />
              <StatCard label="Chat Sessions" value={stats.chat_sessions} testid="stat-sessions" />
              <StatCard label="Phone Calls" value={stats.calls} testid="stat-calls" />
            </div>
            <div className="bg-[#141414] border border-[#27272A] rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <p className="text-xs uppercase tracking-[0.18em] text-[#A3A3A3]">Activity Breakdown</p>
                <button
                  onClick={sendSummary}
                  disabled={sendingSummary}
                  className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] border border-[#D4AF37] text-[#D4AF37] px-4 py-2 rounded-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
                  data-testid="send-recap-button"
                >
                  {sendingSummary && <Loader2 size={14} className="animate-spin" />}
                  Email tonight's recap
                </button>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis dataKey="name" stroke="#A3A3A3" fontSize={12} />
                  <YAxis stroke="#A3A3A3" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    cursor={CHART_CURSOR}
                  />
                  <Bar dataKey="value" fill="#D4AF37" radius={CHART_BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {tab === "appointments" && (
          <div className="bg-[#141414] border border-[#27272A] rounded-lg overflow-x-auto" data-testid="appointments-table">
            <table className="w-full">
              <thead><tr><Th>Type</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Date</Th><Th>Preference</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
              <tbody>
                {appointments.map((a) => (
                  <tr key={a.id}>
                    <Td><span className="text-[#D4AF37]">{a.type === "heaven_on_earth" ? "Heaven" : "Event"}</span></Td>
                    <Td>{a.name}</Td><Td>{a.email}</Td><Td>{a.phone || "—"}</Td>
                    <Td>{a.date || "—"} {a.time}</Td><Td>{a.model_preference || "—"}</Td>
                    <Td>
                      <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded-sm ${
                        a.status === "confirmed" ? "bg-[#10B981]/15 text-[#10B981]"
                        : a.status === "cancelled" ? "bg-[#EF4444]/15 text-[#EF4444]"
                        : "bg-[#D4AF37]/15 text-[#D4AF37]"
                      }`} data-testid={`appt-status-${a.id}`}>{a.status || "pending"}</span>
                    </Td>
                    <Td>
                      <div className="flex gap-2">
                        <button onClick={() => updateStatus(a.id, "confirmed")} className="text-xs text-[#10B981] hover:underline" data-testid={`confirm-appt-${a.id}`}>Confirm</button>
                        <button onClick={() => updateStatus(a.id, "cancelled")} className="text-xs text-[#EF4444] hover:underline" data-testid={`cancel-appt-${a.id}`}>Cancel</button>
                      </div>
                    </Td>
                  </tr>
                ))}
                {appointments.length === 0 && <tr><Td>No appointments yet.</Td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "applicants" && (
          <div className="bg-[#141414] border border-[#27272A] rounded-lg overflow-x-auto" data-testid="applicants-table">
            <table className="w-full">
              <thead><tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Age</Th><Th>Location</Th><Th>Experience</Th><Th>Created</Th></tr></thead>
              <tbody>
                {applicants.map((a) => (
                  <tr key={a.id}>
                    <Td>{a.name}</Td><Td>{a.email}</Td><Td>{a.phone || "—"}</Td><Td>{a.age || "—"}</Td>
                    <Td>{a.location || "—"}</Td><Td>{a.experience || "—"}</Td><Td>{fmt(a.created_at)}</Td>
                  </tr>
                ))}
                {applicants.length === 0 && <tr><Td>No applicants yet.</Td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "leads" && (
          <div className="bg-[#141414] border border-[#27272A] rounded-lg overflow-x-auto" data-testid="leads-table">
            <table className="w-full">
              <thead><tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Topic</Th><Th>Message</Th><Th>Created</Th></tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <Td>{l.name || "—"}</Td><Td>{l.email || "—"}</Td><Td>{l.phone || "—"}</Td>
                    <Td>{l.topic}</Td><Td>{l.message || "—"}</Td><Td>{fmt(l.created_at)}</Td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><Td>No leads yet.</Td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "transcripts" && (
          <div className="space-y-4" data-testid="transcripts-panel">
            {transcripts.map((t) => (
              <details key={t.session_id} className="bg-[#141414] border border-[#27272A] rounded-lg p-5">
                <summary className="cursor-pointer flex justify-between text-sm">
                  <span className="text-[#F5F5F5]">Session {t.session_id.slice(0, 8)}</span>
                  <span className="text-[#A3A3A3]">{t.count} messages · {fmt(t.started)}</span>
                </summary>
                <div className="mt-4 space-y-2">
                  {t.messages.map((m, i) => (
                    <div key={`${t.session_id}-${i}`} className={`text-sm ${m.role === "user" ? "text-[#F5F5F5]" : "text-[#D4AF37]"}`}>
                      <span className="uppercase text-[10px] tracking-widest mr-2 text-[#6b6b6b]">{m.role === "user" ? "Guest" : "Seraphina"}</span>
                      {m.content}
                    </div>
                  ))}
                </div>
              </details>
            ))}
            {transcripts.length === 0 && <p className="text-[#A3A3A3]">No conversations yet.</p>}
          </div>
        )}

        {tab === "calls" && (
          <div className="space-y-4" data-testid="calls-panel">
            {calls.map((c) => (
              <details key={c.call_sid} className="bg-[#141414] border border-[#27272A] rounded-lg p-5">
                <summary className="cursor-pointer flex justify-between text-sm">
                  <span className="text-[#F5F5F5]">{c.from || "Unknown caller"}</span>
                  <span className="text-[#A3A3A3]">{(c.turns || []).length} turns · {c.status || "—"} · {fmt(c.started_at)}</span>
                </summary>
                <div className="mt-4 space-y-2">
                  {(c.turns || []).map((turn, i) => (
                    <div key={`${c.call_sid}-${i}`} className="text-sm space-y-1">
                      <div className="text-[#F5F5F5]"><span className="uppercase text-[10px] tracking-widest mr-2 text-[#6b6b6b]">Caller</span>{turn.caller}</div>
                      <div className="text-[#D4AF37]"><span className="uppercase text-[10px] tracking-widest mr-2 text-[#6b6b6b]">Seraphina</span>{turn.assistant}</div>
                    </div>
                  ))}
                </div>
              </details>
            ))}
            {calls.length === 0 && (
              <p className="text-[#A3A3A3]">No phone calls yet. Once your Twilio number is connected, live call transcripts appear here.</p>
            )}
          </div>
        )}

        {tab === "inbox" && (
          <div className="space-y-4 max-w-3xl" data-testid="inbox-panel">
            {emails.map((e) => (
              <div key={e.id} className="bg-[#141414] border border-[#27272A] rounded-lg p-5" data-testid={`email-${e.id}`}>
                <div className="flex justify-between items-start gap-4 mb-3">
                  <div>
                    <p className="text-[#F5F5F5] font-medium">{e.subject}</p>
                    <p className="text-xs text-[#A3A3A3] mt-1">From {e.from_name} &lt;{e.from}&gt; · {fmt(e.received_at)}</p>
                  </div>
                  <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded-sm shrink-0 ${
                    e.status === "sent" ? "bg-[#10B981]/15 text-[#10B981]"
                    : e.status === "ignored" ? "bg-[#6b6b6b]/15 text-[#9a9a9a]"
                    : "bg-[#D4AF37]/15 text-[#D4AF37]"
                  }`}>{e.status}</span>
                </div>
                <div className="bg-[#0A0A0A] border border-[#1c1c1c] rounded-sm p-3 text-sm text-[#c9c9c9] mb-4 whitespace-pre-wrap">{e.body}</div>
                {e.status === "draft" ? (
                  <>
                    <p className="text-xs uppercase tracking-[0.18em] text-[#A3A3A3] mb-2">Seraphina's draft reply</p>
                    <textarea
                      value={emailDrafts[e.id] || ""}
                      onChange={(ev) => setEmailDrafts((d) => ({ ...d, [e.id]: ev.target.value }))}
                      rows={7}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-sm px-3 py-2.5 text-sm text-[#F5F5F5] outline-none focus:ring-1 focus:ring-[#D4AF37] mb-3"
                      data-testid={`email-draft-${e.id}`}
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={() => sendReply(e.id)}
                        disabled={sendingEmail === e.id}
                        className="flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-semibold px-5 py-2.5 rounded-sm hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
                        data-testid={`email-send-${e.id}`}
                      >
                        {sendingEmail === e.id && <Loader2 size={14} className="animate-spin" />}
                        Approve &amp; Send
                      </button>
                      <button
                        onClick={() => ignoreEmail(e.id)}
                        className="text-sm text-[#A3A3A3] hover:text-[#EF4444] transition-colors"
                        data-testid={`email-ignore-${e.id}`}
                      >
                        Ignore
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-[#A3A3A3] italic">
                    {e.status === "sent" ? "Reply sent." : "Ignored."}
                  </p>
                )}
              </div>
            ))}
            {emails.length === 0 && (
              <p className="text-[#A3A3A3]">No guest emails yet. Once Cloudflare Email Routing is connected, incoming emails and Seraphina's draft replies appear here.</p>
            )}
          </div>
        )}

        {tab === "settings" && (
          <div className="max-w-xl" data-testid="settings-panel">
            <div className="bg-[#141414] border border-[#27272A] rounded-lg p-8 space-y-8">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#A3A3A3] mb-4">Seraphina's Voice</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {voices.map((v) => (
                    <button
                      key={v}
                      onClick={() => setVoice(v)}
                      className={`px-4 py-3 rounded-sm text-sm capitalize transition-colors border ${
                        voice === v
                          ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold"
                          : "bg-[#0A0A0A] text-[#F5F5F5] border-[#27272A] hover:border-[#D4AF37]"
                      }`}
                      data-testid={`voice-option-${v}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#A3A3A3]">Speaking Speed</p>
                  <span className="text-sm text-[#D4AF37]">{speed.toFixed(2)}x</span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="1.5"
                  step="0.05"
                  value={speed}
                  onChange={(e) => setSpeed(parseFloat(e.target.value))}
                  className="w-full accent-[#D4AF37]"
                  data-testid="speed-slider"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={previewVoice}
                  disabled={previewing}
                  className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 rounded-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
                  data-testid="preview-voice-button"
                >
                  {previewing ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                  Preview
                </button>
                <button
                  onClick={saveVoice}
                  disabled={savingVoice}
                  className="flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-semibold px-5 py-2.5 rounded-sm hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
                  data-testid="save-voice-button"
                >
                  {savingVoice && <Loader2 size={16} className="animate-spin" />}
                  Save Voice
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
